import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 25] as const;

interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  hasNext: boolean;
  hasPrev: boolean;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
}

export function TablePagination({
  currentPage,
  totalPages,
  onPrev,
  onNext,
  hasNext,
  hasPrev,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [...DEFAULT_PAGE_SIZE_OPTIONS],
}: TablePaginationProps) {
  const { t } = useTranslation();
  const showSelector = onPageSizeChange !== undefined && pageSize !== undefined;
  const showPager = totalPages > 1;

  if (!showSelector && !showPager) return null;

  return (
    <div className="flex items-center justify-between gap-2 px-4 py-3 border-t text-sm text-muted-foreground">
      {showSelector ? (
        <div className="flex items-center gap-2">
          <span>{t("pagination.rowsLabel")}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger className="h-7 w-16" aria-label={t("pagination.rowsPerPage")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div />
      )}
      {showPager && (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onPrev} disabled={!hasPrev} aria-label={t("pagination.previousPage")}>
            <ChevronLeft className="h-4 w-4" />
            {t("pagination.previous")}
          </Button>
          <span className="px-2" data-testid="pagination-page-info">
            {t("pagination.page")} <span className="font-medium text-foreground">{currentPage}</span> {t("pagination.of")}{" "}
            <span className="font-medium text-foreground">{totalPages}</span>
          </span>
          <Button variant="ghost" size="sm" onClick={onNext} disabled={!hasNext} aria-label={t("pagination.nextPage")}>
            {t("pagination.next")}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
